import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Calendar, Package, Search, Truck, User, X } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService from '@/shared/services/http.service';
import { PackageData, storageStyle, carrierStyle } from '@/lib/packageUtils';
import { PackageDetailsModal } from '@/components/PackageDetailsModal';
import { Input } from '@/components/ui/input';
import { usePageHeader } from '@/contexts/HeaderContext';

// ─── Timeline Filters ─────────────────────────────────────────────────────────
type TimelineFilter = 'all' | 'today' | 'yesterday' | 'this_week' | 'last_2_weeks' | '30days';

const TIMELINE_FILTERS: { value: TimelineFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'timeline_all' },
  { value: 'today', labelKey: 'today' },
  { value: 'yesterday', labelKey: 'yesterday' },
  { value: 'this_week', labelKey: 'this_week' },
  { value: 'last_2_weeks', labelKey: 'last_2_weeks' },
  { value: '30days', labelKey: 'days_30' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getDateRangeForFilter = (filter: TimelineFilter): { startDate?: string; endDate?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const getStartOfDate = (d: Date) => {
    const start = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
    return start.toISOString();
  };

  const getEndOfDate = (d: Date) => {
    const end = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999));
    return end.toISOString();
  };

  switch (filter) {
    case 'today':
      return { startDate: getStartOfDate(today), endDate: getEndOfDate(today) };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { startDate: getStartOfDate(yesterday), endDate: getEndOfDate(yesterday) };
    }
    case 'this_week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return { startDate: getStartOfDate(weekStart), endDate: getEndOfDate(today) };
    }
    case 'last_2_weeks': {
      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(today.getDate() - 14);
      return { startDate: getStartOfDate(twoWeeksAgo), endDate: getEndOfDate(today) };
    }
    case '30days': {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      return { startDate: getStartOfDate(thirtyDaysAgo), endDate: getEndOfDate(today) };
    }
    default:
      return {};
  }
};

const formatPickedDateTime = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return (
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const PickedPackagesList = ({ standalone = false }: { standalone?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [packages, setPackages] = useState<PackageData[]>([]);
  const [totalPackages, setTotalPackages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const isModalOpen = selectedPackage !== null;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  const { ref: loadMoreRef, inView } = useInView({ threshold: 0, rootMargin: '100px' });

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    setShowScrollTop(scrollContainerRef.current.scrollTop > 300);
  }, []);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchPackages = useCallback(
    async (query?: string, pageToLoad: number = 1) => {
      if (pageToLoad === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const params: Record<string, string> = {
          sortBy: 'deletedAt',
          order: 'desc',
          pageSize: '20',
          pageNumber: pageToLoad.toString(),
          packageState: 'picked',
        };

        const activeQuery = query !== undefined ? query : searchQuery;
        if (activeQuery) params.searchQuery = activeQuery;

        const dateRange = getDateRangeForFilter(timelineFilter);
        if (dateRange.startDate) params.from = dateRange.startDate;
        if (dateRange.endDate) params.to = dateRange.endDate;

        const response = await HttpService.get(API_CONFIG.path.packageList, params);
        const all: PackageData[] = Array.isArray(response.data) ? response.data : response.data?.data || [];

        setTotalPackages(response.pagination?.total || 0);

        if (pageToLoad === 1) {
          setPackages(all);
        } else {
          setPackages((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            return [...prev, ...all.filter((p) => !existingIds.has(p.id))];
          });
        }
        setHasMore(all.length === 20);
      } catch {
        if (pageToLoad === 1) setPackages([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [timelineFilter, searchQuery],
  );

  // Fetch on filter change
  useEffect(() => {
    setPage(1);
    fetchPackages(undefined, 1);
  }, [timelineFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchPackages(searchQuery, 1);
    }, 500);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    if (inView && hasMore && !loading && !loadingMore && packages.length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPackages(searchQuery, nextPage);
    }
  }, [inView, hasMore, loading, loadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const headerTitle = useMemo(() => <>✅ {t('picked_packages')}</>, [t]);
  const handleHeaderBack = useCallback(() => navigate('/'), [navigate]);
  const headerContent = useMemo(
    () => (standalone ? { title: headerTitle, onBack: handleHeaderBack } : {}),
    [standalone, headerTitle, handleHeaderBack],
  );

  usePageHeader(headerContent);

  return (
    <div
      className={standalone ? 'min-h-[calc(100vh-73px)] bg-background flex flex-col items-center' : 'relative w-full'}
    >
      <div className={standalone ? 'relative bg-background px-4 py-3 w-full' : 'relative w-full'}>
        {/* Search bar */}
        <div className="flex gap-2 w-full mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder={t('search_packages')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 bg-white h-[40px]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Total Packages */}
          <div className="bg-white px-3 rounded-md border border-border/50 shadow-[var(--shadow-card)] shrink-0 flex justify-center items-center align-center text-sm h-[40px]">
            {totalPackages} {t('packages')}
          </div>
        </div>

        {/* Timeline Filter Chips */}
        <div className="bg-card rounded-md p-2 shadow-[var(--shadow-card)] border border-border/50 w-full mb-3">
          <div className="flex flex-nowrap items-center gap-1 overflow-x-auto overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TIMELINE_FILTERS.map(({ value, labelKey }) => (
              <button
                key={value}
                onClick={() => setTimelineFilter(value)}
                className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  timelineFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/70'
                }`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {packages.length > 0 && (
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={`overflow-y-auto transition-opacity duration-200 ${standalone ? 'h-[calc(100vh-210px)]' : 'h-[calc(100vh-320px)]'} ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
              {packages.map((pkg) => {
                const { bg, border, text } = storageStyle(pkg.storageOption);
                const carrier = pkg.packageMetadata?.carrier;
                const carrierSty = carrier ? carrierStyle(carrier) : null;

                return (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPackage(pkg);
                    }}
                    key={pkg.id}
                    className="relative rounded-xl border border-border bg-white transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 cursor-pointer p-4"
                  >
                    {/* Package number — large & prominent */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold leading-none text-foreground calculator-font">
                          {pkg.packageNumber || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Carrier */}
                        {carrier && carrierSty && (
                          <div
                            className={`flex items-center gap-2 px-2 py-0.5 rounded-full border w-max text-xs ${carrierSty.bg} ${carrierSty.border} ${carrierSty.text}`}
                          >
                            <Truck className="w-4 h-4" />
                            <p className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold first-letter:uppercase">
                              {carrier}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* BIN + Storage — single row, no wrap */}
                    <div className="py-2 flex items-center gap-1.5 min-h-9">
                      {pkg.frameNumber && (
                        <span className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-primary text-foreground w-fit">
                          {pkg.frameNumber}
                        </span>
                      )}
                      {pkg.boxNumber && (
                        <span className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-indigo-700 text-foreground w-fit">
                          {pkg.boxNumber}
                        </span>
                      )}
                      {pkg.storageOption && (
                        <span
                          className={`flex items-center text-sm font-bold px-2 py-0.5 rounded-full border ${bg} ${border} ${text}`}
                        >
                          {t(pkg.storageOption.toLowerCase())}
                        </span>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="pb-2">
                      <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-sm">
                        <div className="w-7 h-7 rounded-l-sm p-0.5 flex justify-center items-center">
                          <User className="w-5 h-5 text-blue-500" />
                        </div>
                        <span className="text-md font-medium text-foreground p-0.5 truncate max-w-[90%] whitespace-nowrap">
                          {pkg.packageMetadata?.receiver?.name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2 mt-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                          <p className="text-md text-muted-foreground">{formatPickedDateTime(pkg.deletedAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite Scroll Trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="py-6 flex justify-center items-center">
                {loadingMore && <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />}
              </div>
            )}
            {!hasMore && packages.length > 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('no_more_packages', 'No more packages')}
              </div>
            )}
          </div>
        )}

        {/* Empty states */}
        {!loading && packages.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">{t('no_packages_found')}</p>
          </div>
        )}
        {loading && packages.length === 0 && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2" />
            <p className="text-muted-foreground">{t('loading_packages')}</p>
          </div>
        )}

        {/* Scroll to top */}
        {showScrollTop && (
          <div className="absolute bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <button
              onClick={scrollToTop}
              className="w-10 h-10 rounded-full shadow-lg bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-300 flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Details Modal */}
        <PackageDetailsModal
          isOpen={isModalOpen}
          onClose={() => setSelectedPackage(null)}
          pkg={selectedPackage}
          isEditing={false}
          isPickedButton={false}
          actionButtonText=""
          onActionClick={() => {}}
        />
      </div>
      {/* closes inner content div for standalone */}
    </div>
  );
};

export default PickedPackagesList;
